import React from "react";
import "@/App.css";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
// NOTE: React Query DevTools disabled temporarily due to locale initialization error
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import AcceptInvitePage from "@/pages/AcceptInvite";
import PlaybooksPage from "@/pages/Playbooks";
import PlaybookScriptsPage from "@/pages/PlaybookScripts";
import AdminConsolePage from "@/pages/AdminConsole";
import AnalyticsPage from "@/pages/Analytics";
import AnalyticsWarRoom from "@/pages/AnalyticsWarRoom";
import ArchitectureMemoryPage from "@/pages/ArchitectureMemoryPage";
import RunsPage from "@/pages/Runs";
import LeadsPage from "@/pages/Leads";
import LeadDetailsPage from "@/pages/LeadDetails";
import SettingsPage from "@/pages/Settings";
import BrandSettingsPage from "@/pages/BrandSettingsPage";
import DomainsSettingsPage from "@/pages/DomainsSettingsPage";
import AdvertisingPage from "@/pages/AdvertisingPage";
// LPCopyEditorPage removed — now inline in ProfitPathBuilder Landing Page step
// SocialProofPage removed — now inline in ProfitPathBuilder Landing Page step
import AppointmentsPage from "@/pages/Appointments";
import CalendarPage from "@/pages/CalendarPage";
import AppointmentDetailsPage from "@/pages/AppointmentDetails";
import QuoteTemplateBuilder from "@/pages/QuoteTemplateBuilder";
import ClientsPage from "@/pages/Clients";
import ClientDetailsPage from "@/pages/ClientDetails";
import FunnelsPage from "@/pages/FunnelsPage";
import FunnelDetailsPage from "@/pages/FunnelDetailsPage";
import CampaignsPage from "@/pages/CampaignsPage";
// MA-ARCH-130: Profit Paths
import ProfitPathsPage from "@/pages/ProfitPathsPage";
import ProfitPathDetailPage from "@/pages/ProfitPathDetailPage";
// MA-UI-056: Profit Path Builder
import ProfitPathBuilder from "@/pages/ProfitPathBuilder";
// MA-LP-BUILDER-010C: Standalone Landing Page Builder
import LandingPageBuilder from "@/pages/LandingPageBuilder";
import LandingPagesPage from "@/pages/LandingPagesPage";
import AIReviewPage from "@/pages/AIReviewPage";
// MA-AI-401: Experiments (Performance & Optimization)
import ExperimentsPage from "@/pages/ExperimentsPage";
// FunnelBuilderPage removed - replaced by FunnelDetailsPage with tabs (MA-UI-023)
// FunnelAnalyticsPage removed - now part of FunnelDetailsPage and War Room (MA-UI-023)
import ProductDetailsPage from "@/pages/ProductDetails";
import CommissionSetupPage from "@/pages/CommissionSetup";
import ScriptEditPage from "@/pages/ScriptEdit";
import ScriptPlayPage from "@/pages/ScriptPlay";
// MA-UI-078: System Activation
import ActivationPage from "@/pages/ActivationPage";
// MA-PHASE11A: Account Management
import ManageAccountsPage from "@/pages/ManageAccountsPage";
import AppShell from "@/components/AppShell";
import BookingWidgetDemo from "@/pages/BookingWidgetDemo";
import BookingWidgetEmbed from "@/pages/BookingWidgetEmbed";
import ConversationFunnelEmbed from "@/pages/ConversationFunnelEmbed";
import { getToken, getStoredUser } from "@/lib/auth";
import { RunExitGuardProvider } from "@/contexts/RunExitGuardContext";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { RequireActivationAccess, RequireModule, RequireModuleLevel } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { getFirstReachablePath, isPathReachable } from "@/lib/navigationConfig";
import AgentWorkspaceRoute from "@/features/agent-workspace/AgentWorkspaceRoute";
import EmailsShell from "@/features/emails/EmailsShell";

// Marketing site pages
import {
  MarketingLayout,
  HomePage,
  FeaturesPage,
  UseCasesPage,
  DemoPage,
} from "@/pages/marketing";

// Determine if we should show React Query DevTools (only in development)
const isDevelopment = process.env.NODE_ENV === "development";

//: Key the deep-link destination is parked under while the user authenticates.
//: Produced by ProtectedLayout, ProtectedFullScreenLayout and lib/api.js;
//: consumed HERE, which is the only place both the destination and the
//: authorization needed to judge it are available.
const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

function takePostLoginRedirect() {
  if (typeof window === "undefined") return null;
  try {
    const saved = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (saved) window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return saved;
  } catch {
    // Private mode / blocked storage: no deep link, not an error.
    return null;
  }
}

/**
 * P4-WP-9A.1: the single canonical default-destination resolver.
 *
 * MA-PHASE11A-FIX-013 made this an unconditional `/app/warroom` redirect for
 * every user -- "no conditional logic, no permission checks". That held only
 * while every role could reach War Room. P4-WP-9A set `war_room: "off"` for
 * Sales Agent, so the hard redirect now lands that role on Access Denied on
 * login, on refresh, on the logo, and on the /app catch-all.
 *
 * The resolver derives the destination from EFFECTIVE AUTHORIZATION, not from
 * the role: `getFirstReachablePath` walks NAV_DESTINATIONS in rail order and
 * applies the same `isDestinationVisible` predicate the rail itself uses --
 * module destinations against `effectiveModules`, and Agent Workspace against
 * its pre-existing rollout gate. There is deliberately NO role -> route matrix
 * here; adding one would put a second copy of authorization in the router.
 *
 * Today that yields War Room for every role that still holds it, Agent
 * Workspace for a Sales Agent whose Account is in the rollout gate, and Leads
 * for one whose Account is not -- all without naming a single role.
 *
 * This is NOT an authorization boundary. Every destination remains guarded by
 * its own RequireModule; this only chooses where to send someone who did not
 * ask for anything specific.
 */
function DefaultHomeRedirect() {
  const { selectedTenantId } = useTenant();
  const { effectiveModules } = usePermissions();

  // ProtectedLayoutInner gates on tenant + permissions before rendering the
  // Outlet, so both values are resolved by the time this runs.
  const saved = React.useMemo(() => takePostLoginRedirect(), []);

  // A parked deep link is honoured only if the caller can still reach it.
  // "/app" is excluded because it resolves back to THIS component, and a
  // non-/app value is left alone -- those belong to flows (activation, invite
  // acceptance) that module reachability does not describe, and the previous
  // behaviour already declined to replay them.
  if (
    saved &&
    saved !== "/app" &&
    saved.startsWith("/app") &&
    isPathReachable(saved, effectiveModules, selectedTenantId)
  ) {
    return <Navigate to={saved} replace />;
  }

  const target = getFirstReachablePath(effectiveModules, selectedTenantId);

  // `getFirstReachablePath` returns the "/app" sentinel when NOTHING is
  // visible. Navigating there would re-enter this component forever, so the
  // no-destination case terminates in a rendered state instead. It grants
  // nothing: no module guard is bypassed and no route is entered.
  if (target === "/app") {
    return (
      <div
        className="flex items-center justify-center min-h-[60vh]"
        data-testid="no-accessible-destination"
      >
        <div className="text-center space-y-3 max-w-md px-6">
          <h1 className="text-2xl font-semibold text-foreground">
            No accessible modules
          </h1>
          <p className="text-muted-foreground">
            Your account does not currently have access to any area of the
            application.
          </p>
          <p className="text-sm text-muted-foreground">
            Contact your account administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return <Navigate to={target} replace />;
}

function ProtectedLayout() {
  const token = getToken();
  if (!token) {
    // Preserve deep-link destination so we can return after login
    const dest = window.location.pathname + window.location.search;
    sessionStorage.setItem("post_login_redirect", dest);
    return <Navigate to="/login" replace />;
  }

  // MA-ARCH-201 Phase 3.5: Onboarding gate for state-driven entry control
  // TEMPORARILY DISABLED to test Account Switcher - will re-enable after integration
  return (
    <TenantProvider>
      <RunExitGuardProvider>
        <AppShell>
          <ProtectedLayoutInner />
        </AppShell>
      </RunExitGuardProvider>
    </TenantProvider>
  );
}

/**
 * MA-PHASE11A-POSTLOGIN-ROUTING-FIX-012: Boot Guard (FIXED)
 * 
 * Block rendering of protected pages until BOTH account context AND permissions are loaded.
 * This prevents premature API calls and routing decisions before full context is ready.
 */
function ProtectedLayoutInner() {
  const { loading: tenantLoading, selectedTenantId } = useTenant();
  const { isLoading: permissionsLoading } = usePermissions();
  
  // MA-PHASE11A-FIX-012: Wait for BOTH tenant context AND permissions before rendering
  // This ensures DefaultHomeRedirect has all data it needs to make routing decisions
  // and prevents pages from mounting with incomplete context
  if (tenantLoading || !selectedTenantId || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-sm text-slate-200/70">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Both account context and permissions are ready - render the protected pages
  return <Outlet />;
}

// MA-UI-078: Protected layout without AppShell (for full-screen pages like Activation)
function ProtectedFullScreenLayout() {
  const token = getToken();
  if (!token) {
    const dest = window.location.pathname + window.location.search;
    sessionStorage.setItem("post_login_redirect", dest);
    return <Navigate to="/login" replace />;
  }
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

const router = createBrowserRouter([
  // Marketing site routes (public)
  {
    path: "/",
    element: <MarketingLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "features", element: <FeaturesPage /> },
      { path: "use-cases", element: <UseCasesPage /> },
      { path: "demo", element: <DemoPage /> },
    ],
  },
  // Auth routes
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/accept-invite", element: <AcceptInvitePage /> },
  // Booking widget routes
  { path: "/booking-demo", element: <BookingWidgetDemo /> },
  { path: "/book", element: <BookingWidgetEmbed /> },
  // Conversation funnel embed route
  { path: "/funnel", element: <ConversationFunnelEmbed /> },
  // MA-UI-078: System Activation (full-screen, no AppShell)
  {
    path: "/activation",
    element: <ProtectedFullScreenLayout />,
    children: [
      {
        index: true,
        element: (
          <RequireActivationAccess>
            <ActivationPage />
          </RequireActivationAccess>
        ),
      },
    ],
  },
  // Protected app routes
  // MA-ARCH-201 Phase 3: Routes wrapped with permission guards
  {
    path: "/app",
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <DefaultHomeRedirect /> },
      { path: "agent-workspace", element: <AgentWorkspaceRoute /> },
      // EMAILS-IMPL-001: registered as the 17th governed module (Spencer-approved
      // registry expansion) -- off/full binary, same pattern as campaigns/etc.
      { path: "emails", element: <RequireModule moduleId="emails" moduleName="Emails"><EmailsShell /></RequireModule> },
      // MA-RBAC-POST-BATCH3-FRONTEND-CONSISTENCY-AUDIT-124: Canonical module IDs
      // Playbooks / Runs / Scripts module routes
      { 
        path: "playbooks", 
        element: <RequireModule moduleId="playbooks" moduleName="Playbooks"><PlaybooksPage /></RequireModule>
      },
      { 
        path: "playbooks/:playbookId", 
        element: <RequireModule moduleId="playbooks" moduleName="Playbooks"><PlaybookScriptsPage /></RequireModule>
      },
      { 
        path: "runs", 
        element: <RequireModule moduleId="runs" moduleName="Runs"><RunsPage /></RequireModule>
      },
      { 
        path: "scripts/:id/edit", 
        element: <RequireModule moduleId="playbooks" moduleName="Scripts"><ScriptEditPage /></RequireModule>
      },
      { 
        path: "scripts/:id/play", 
        element: <RequireModule moduleId="runs" moduleName="Scripts"><ScriptPlayPage /></RequireModule>
      },
      // Leads module routes
      { 
        path: "leads", 
        element: <RequireModule moduleId="leads" moduleName="Leads"><LeadsPage /></RequireModule>
      },
      { 
        path: "leads/:leadId", 
        element: <RequireModule moduleId="leads" moduleName="Lead Details"><LeadDetailsPage /></RequireModule>
      },
      { 
        path: "appointments", 
        element: <RequireModule moduleId="appointments" moduleName="Appointments"><AppointmentsPage /></RequireModule>
      },
      { 
        path: "appointments/:appointmentId", 
        element: <RequireModule moduleId="appointments" moduleName="Appointment Details"><AppointmentDetailsPage /></RequireModule>
      },
      { 
        path: "calendar", 
        element: <RequireModule moduleId="calendar" moduleName="Calendar"><CalendarPage /></RequireModule>
      },
      { 
        path: "clients", 
        element: <RequireModule moduleId="clients" moduleName="Clients"><ClientsPage /></RequireModule>
      },
      { 
        path: "clients/:clientId", 
        element: <RequireModule moduleId="clients" moduleName="Client Details"><ClientDetailsPage /></RequireModule>
      },
      { 
        path: "clients/:clientId/products/:productIndex", 
        element: <RequireModule moduleId="clients" moduleName="Product Details"><ProductDetailsPage /></RequireModule>
      },
      { 
        path: "quote-templates/new", 
        element: <RequireModule moduleId="clients" moduleName="Quote Templates"><QuoteTemplateBuilder /></RequireModule>
      },
      { 
        path: "commission-setup", 
        element: <RequireModule moduleId="commission_profiles" moduleName="Commission Setup"><CommissionSetupPage /></RequireModule>
      },
      // Campaigns module routes
      { 
        path: "campaigns", 
        element: <RequireModule moduleId="campaigns" moduleName="Campaigns"><CampaignsPage /></RequireModule>
      },
      { 
        path: "advertising", 
        element: <RequireModule moduleId="advertising" moduleName="Advertising"><AdvertisingPage /></RequireModule>
      },
      { 
        path: "experiments", 
        element: <RequireModule moduleId="experiments" moduleName="Experiments"><ExperimentsPage /></RequireModule>
      },
      // Analytics module routes
      { path: "analytics", element: <Navigate to="/app/warroom" replace /> },
      // War Room (canonical: war_room; was deprecated alias 'analytics')
      { 
        path: "warroom", 
        element: <RequireModule moduleId="war_room" moduleName="War Room"><AnalyticsWarRoom /></RequireModule>
      },
      { 
        path: "profit-paths", 
        element: <RequireModule moduleId="profit_paths" moduleName="Profit Paths"><ProfitPathsPage /></RequireModule>
      },
      { 
        path: "profit-paths/:id/builder", 
        element: (
          <RequireModuleLevel moduleId="profit_paths" requiredLevel="full" moduleName="Profit Path Builder">
            <ProfitPathBuilder />
          </RequireModuleLevel>
        )
      },
      { 
        path: "profit-paths/:id", 
        element: <RequireModule moduleId="profit_paths" moduleName="Profit Path Details"><ProfitPathDetailPage /></RequireModule>
      },
      // Funnels module routes (canonical: funnels; was deprecated alias 'funnel_analytics')
      { 
        path: "funnels", 
        element: <RequireModule moduleId="funnels" moduleName="Funnels"><FunnelsPage /></RequireModule>
      },
      { 
        path: "funnels/:id", 
        element: <RequireModule moduleId="funnels" moduleName="Funnel Details"><FunnelDetailsPage /></RequireModule>
      },
      { 
        path: "funnels/:id/review", 
        element: <RequireModule moduleId="funnels" moduleName="AI Review"><AIReviewPage /></RequireModule>
      },
      { 
        path: "landing-pages/:landing_page_id", 
        element: <RequireModule moduleId="landing_pages" moduleName="Landing Page Builder"><LandingPageBuilder /></RequireModule>
      },
      { 
        path: "landing-pages", 
        element: <RequireModule moduleId="landing_pages" moduleName="Landing Pages"><LandingPagesPage /></RequireModule>
      },
      { path: "funnel-analytics", element: <Navigate to="/app/warroom?tab=funnels" replace /> },
      // Settings module routes
      { 
        path: "settings", 
        element: <RequireModule moduleId="settings" moduleName="Settings"><SettingsPage /></RequireModule>
      },
      { 
        path: "settings/brand", 
        element: <RequireModule moduleId="settings" moduleName="Brand Settings"><BrandSettingsPage /></RequireModule>
      },
      { 
        path: "settings/domains", 
        element: <RequireModule moduleId="settings" moduleName="Domain Settings"><DomainsSettingsPage /></RequireModule>
      },
      // Admin module routes (canonical: admin; was deprecated alias 'team')
      { 
        path: "admin", 
        element: <RequireModule moduleId="admin" moduleName="Admin Console"><AdminConsolePage /></RequireModule>
      },
      // MA-PHASE11A: Account management (requires admin module)
      { 
        path: "accounts", 
        element: <RequireModule moduleId="admin" moduleName="Manage Accounts"><ManageAccountsPage /></RequireModule>
      },
      // Dashboard route (no guard - available to all)
      { path: "dashboard", element: <DefaultHomeRedirect /> },
      // Legacy/utility routes (admin-only debug tools)
      { 
        path: "architecture-memory", 
        element: <RequireModule moduleId="admin" moduleName="Architecture Memory"><ArchitectureMemoryPage /></RequireModule>
      },
      { path: "product-types/:productTypeId", element: <DefaultHomeRedirect /> },
      { path: "*", element: <DefaultHomeRedirect /> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-app" data-testid="app-root">
        <RouterProvider router={router} />
        <Toaster />
      </div>
      {/* DevTools disabled due to locale error - enable when issue is resolved */}
      {/* {isDevelopment && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />} */}
    </QueryClientProvider>
  );
}
